/**
 * RBAC-UI matrix (US-AE-IMP · @UC9002) — ma trận vai→hiển thị dùng cho ADMIN UI.
 *
 * MỤC ĐÍCH: điều khiển UI-ONLY — nav item nào 1 vai THẤY, action nào ENABLE. KHÔNG phải ranh giới
 * bảo mật: server `@Roles` guard vẫn là chốt thật (fail-closed). Chỉnh matrix chỉ đổi UX (ẩn nav
 * không dùng được, disable nút không đủ quyền) → tránh lỗi Forbidden khó hiểu, KHÔNG mở quyền backend.
 *
 * LƯU TRỮ: 1 setting JSON trong system_settings key `admin_rbac_ui_matrix` (SUPER_ADMIN sửa runtime
 * qua trang config; default seed dưới đây mirror @Roles hiện tại). Đọc: mọi vai (để render nav của
 * chính mình). Ghi: chỉ SUPER_ADMIN (system-settings PATCH đã @Roles(SUPER_ADMIN)).
 */
import { z } from "zod";
import { adminRoleSchema, type AdminRole } from "./admin-rbac.js";

/** Key setting trong system_settings (chỉ [a-z0-9_]). */
export const ADMIN_RBAC_UI_MATRIX_KEY = "admin_rbac_ui_matrix";

/** 4 vai admin (mirror adminRoleSchema) — tiện lặp UI. */
export const ADMIN_ROLES: readonly AdminRole[] = [
  "SUPER_ADMIN",
  "OPS",
  "SUPPORT",
  "READ_ONLY",
] as const;

/**
 * 1 entry matrix cho 1 nav-key: danh sách vai được THẤY (view) + (tùy chọn) vai được thao-tác (act).
 * `view` rỗng ⇒ ẩn với mọi vai. `act` vắng ⇒ mọi vai thấy được cũng thao tác được (đa số list read-only).
 */
export const rbacMatrixEntrySchema = z.object({
  view: z.array(adminRoleSchema),
  act: z.array(adminRoleSchema).optional(),
});
export type RbacMatrixEntry = z.infer<typeof rbacMatrixEntrySchema>;

/** Matrix = map navKey → entry. Key là nav `key` (n.*) trong components/layout/nav.ts. */
export const adminRbacUiMatrixSchema = z.record(z.string(), rbacMatrixEntrySchema);
export type AdminRbacUiMatrix = z.infer<typeof adminRbacUiMatrixSchema>;

// Nhóm vai tiện dùng khi khai default.
const ALL: AdminRole[] = ["SUPER_ADMIN", "OPS", "SUPPORT", "READ_ONLY"];
const SUPER_ONLY: AdminRole[] = ["SUPER_ADMIN"];
const SUPER_OPS: AdminRole[] = ["SUPER_ADMIN", "OPS"];

/**
 * DEFAULT matrix — mirror @Roles server (read-access) tại thời điểm seed:
 *  - Nhạy cảm (quản trị admin / cấu hình hệ thống / secrets): CHỈ SUPER_ADMIN thấy.
 *  - Còn lại: mọi vai thấy (đa số list GET mở 4 vai). `act` = SUPER_ADMIN+OPS cho màn có mutation
 *    (nút reprocess/replace/re-run…) — SUPPORT/READ_ONLY thấy nhưng nút bị disable.
 * SUPER_ADMIN chỉnh runtime qua trang config nếu muốn siết/nới thêm.
 */
export const DEFAULT_ADMIN_RBAC_UI_MATRIX: AdminRbacUiMatrix = {
  "n.dashboard": { view: ALL },
  // AE1 — quản trị & quyền: nhạy cảm → SUPER_ADMIN.
  "n.admins": { view: SUPER_ONLY },
  "n.allowlist": { view: SUPER_ONLY },
  // Trang chỉnh chính matrix — CHỈ SUPER_ADMIN thấy + sửa (act).
  "n.rbac_matrix": { view: SUPER_ONLY, act: SUPER_ONLY },
  "n.audit_action": { view: ALL },
  // Cấu hình hệ thống — SUPER_ADMIN.
  "n.settings": { view: SUPER_ONLY },
  "n.flags": { view: ALL, act: SUPER_OPS },
  "n.locale": { view: ALL, act: SUPER_OPS },
  "n.overrides": { view: ALL, act: SUPER_OPS },
  // Người dùng & nhóm — read mở 4 vai.
  "n.users": { view: ALL, act: SUPER_OPS },
  "n.gdpr": { view: SUPER_OPS },
  "n.identity": { view: ALL },
  "n.groups": { view: ALL, act: SUPER_OPS },
  "n.funds": { view: ALL, act: SUPER_OPS },
  "n.member_imports": { view: SUPER_OPS },
  // Sổ cái / tài chính — read 4 vai; re-run/mutation SUPER_ADMIN+OPS.
  "n.reconcile": { view: ALL, act: SUPER_OPS },
  // 🔴 US-E21.F7-0219: debts (nợ chéo) + expenses (đợt chi) — KHAI để chặn fail-open (READ_ONLY/SUPPORT
  // trước đây thấy vì chưa khai → matrix fail-open). Read 4 vai; mutation (nếu có) SUPER_ADMIN+OPS.
  "n.debts": { view: ALL, act: SUPER_OPS },
  "n.expenses": { view: ALL, act: SUPER_OPS },
  // Chi tiết chia suất 1 khoản chi + can thiệp (REVERSAL+repost): xem 4 vai (tiền redact theo vai ở
  // server); CAN THIỆP chỉ SUPER_ADMIN+OPS (server @Roles là chốt thật).
  "n.expenseSplits": { view: ALL, act: SUPER_OPS },
  "n.drift": { view: ALL, act: SUPER_OPS },
  "n.cashflow": { view: ALL },
  "n.idempotency": { view: ALL },
  "n.payments": { view: ALL, act: SUPER_OPS },
  "n.collections": { view: ALL },
  // 🔴 US-AE16.F2-0370 (@UC9635 @UC9636): lịch thu định kỳ = NGUỒN SINH nghĩa vụ tiền. XEM cho 4 vai
  // (tiền redact theo vai ở server — SUPPORT cần thấy để trả lời "sao nhóm tôi đóng rồi vẫn bị đòi
  // tiền?"); THAO TÁC (van tay "Tạm dừng") chỉ SUPER_ADMIN+OPS — @Roles ở server là chốt THẬT, đây
  // chỉ là lớp hiển thị. KHAI TƯỜNG MINH để không rơi vào fail-open (nav-key chưa khai ⇒ mọi vai thấy).
  "n.collectionSchedules": { view: ALL, act: SUPER_OPS },
  "n.voting": { view: ALL },
  // 🔴 US-E20.F6-0212 (@UC9343–@UC9345): 5 cỗ máy chuyên biệt — XEM 4 vai (tiền redact theo vai ở
  // server); CAN THIỆP (vỡ hụi/khoá earmark/từ chối NAV/đổi ghế) chỉ SUPER_ADMIN+OPS (server @Roles
  // là chốt thật).
  "n.machines": { view: ALL, act: SUPER_OPS },
  "n.disputes": { view: ALL, act: SUPER_OPS },
  "n.corrections": { view: ALL, act: SUPER_OPS },
  // Vận hành / kiểm duyệt.
  "n.inbox": { view: ALL },
  "n.risk": { view: ALL, act: SUPER_OPS },
  "n.abuse": { view: ALL, act: SUPER_OPS },
  "n.moderation": { view: ALL, act: SUPER_OPS },
  "n.transparency": { view: ALL, act: SUPER_OPS },
  "n.referrals": { view: ALL },
  "n.onboarding": { view: ALL },
  "n.analyticsGrowth": { view: ALL },
  "n.analyticsReferrals": { view: ALL },
  "n.analyticsGroupHealth": { view: ALL },
  // Truyền thông.
  "n.broadcast": { view: ALL, act: SUPER_OPS },
  "n.quietHours": { view: ALL, act: SUPER_OPS },
  "n.outbox": { view: ALL, act: SUPER_OPS },
  "n.channels": { view: ALL, act: SUPER_OPS },
  "n.templates": { view: ALL, act: SUPER_OPS },
  // AI.
  "n.ai_orch": { view: ALL, act: SUPER_OPS },
  // 🔴 US-AI-0294 (@UC9417): "Cấu hình AI" (/ai/kill-switch) — KHAI để chặn fail-open, cùng họ với
  // US-E21.F7-0219 (n.debts/n.expenses). Trước đây trang chỉ ĐỌC nên thiếu khai còn vô hại; nay nó là
  // surface GHI (tắt/bật kill-switch AI toàn cục, flag T1, per-group) ⇒ `act` mới thực sự có nghĩa.
  // Read 4 vai (ai cũng cần thấy AI đang bật/tắt); THAO TÁC SUPER_ADMIN+OPS — mirror n.ai_orch và
  // khớp @Roles server của PATCH /admin/ai/runtime-enabled (server vẫn là chốt thật).
  "n.ai_killswitch": { view: ALL, act: SUPER_OPS },
  "n.ai_chats": { view: ALL },
  "n.ai_models": { view: ALL, act: SUPER_OPS },
  "n.ai_audit": { view: ALL },
  "n.ai_cost": { view: ALL },
  "n.ai_eval": { view: ALL, act: SUPER_OPS },
  "n.ai_safety": { view: ALL },
  // 🔴 US-AI-0310-S06 (@UC9400 · @gate config) — config deep-link AI theo iOS/Android/Web. KHAI để
  // chặn fail-OPEN (nav-key chưa khai ⇒ hiện với MỌI vai). Read 4 vai; THAO TÁC chỉ SUPER_ADMIN —
  // đổi link dẫn nhầm cả hệ thống, cùng mức rủi ro PUT /admin/rbac-ui-matrix (server @Roles vẫn chốt).
  "n.ai_deeplinks": { view: ALL, act: SUPER_ONLY },
  // Billing.
  "n.billing": { view: ALL, act: SUPER_OPS },
  "n.usageEconomics": { view: ALL },
  // Hạ tầng / quan sát.
  "n.ci": { view: ALL },
  "n.metrics": { view: ALL },
  "n.alerts": { view: ALL },
  "n.threats": { view: ALL },
  // 🔴 US-E19.F7-0206 (@UC9320): security console — XEM 4 vai (tiền redact theo vai ở server);
  // CAN THIỆP (freeze/huỷ cooling/force-resolve) chỉ SUPER_ADMIN+OPS (server @Roles là chốt thật).
  "n.securityConsole": { view: ALL, act: SUPER_OPS },
  "n.auditlog": { view: ALL },
  "n.backup": { view: SUPER_OPS },
  "n.secrets": { view: SUPER_ONLY },
  // Từ điển — read 4 vai; sửa (3 DB-backed) SUPER_ADMIN+OPS.
  "n.d_income": { view: ALL, act: SUPER_OPS },
  "n.d_expense": { view: ALL, act: SUPER_OPS },
  "n.d_coa": { view: ALL, act: SUPER_OPS },
  "n.d_enum": { view: ALL },
  "n.d_error": { view: ALL },
  "n.d_blocklist": { view: ALL, act: SUPER_OPS },
  "n.d_reason": { view: ALL },
  "n.d_refch": { view: ALL },
  "n.d_banks": { view: ALL },
};

/**
 * 1 vai có THẤY nav-key? Fail-safe: key KHÔNG có trong matrix → HIỆN (tránh ẩn oan item mới chưa khai;
 * bảo mật thật do server lo). Vai undefined (chưa rõ) → theo default matrix, coi như thấy nếu ALL.
 */
export function canViewNav(
  matrix: AdminRbacUiMatrix,
  role: AdminRole | undefined,
  navKey: string,
): boolean {
  const entry = matrix[navKey];
  if (!entry) return true; // chưa khai → không ẩn oan (fail-open UI; server vẫn chặn)
  if (!role) return false; // vai không xác định + có khai → ẩn (least-privilege UI)
  return entry.view.includes(role);
}

/**
 * 1 vai có được THAO TÁC (enable nút) trên màn nav-key? `act` vắng ⇒ theo `view` (thấy = thao tác).
 * Fail-safe giống canViewNav. Server vẫn là chốt (403 nếu vai không đủ) — đây chỉ disable UX.
 */
export function canActNav(
  matrix: AdminRbacUiMatrix,
  role: AdminRole | undefined,
  navKey: string,
): boolean {
  const entry = matrix[navKey];
  if (!entry) return true;
  if (!role) return false;
  const allowed = entry.act ?? entry.view;
  return allowed.includes(role);
}
